// WhatsAppContentSelector.tsx - DIRECT API VERSION (Full Replacement)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { shareContentLead } from '../../services/leadService';
import { downloadContentFile, contentBlobToFile, getAllContent } from '../../services/contentService';
import PopupComponent from '../../components/PopupComponent';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';

interface Lead {
  _id: string;
  name?: string;
  phone?: string;
  [key: string]: any;
}

interface ContentItem {
  _id: string;
  name?: string;
  type: 'file' | 'page';  // 🔥 REMOVED: 'message' and 'whatsapp_template'
  details?: {
    title?: string;
    description?: string;
  };
  files?: Array<{
    filePath: string;
    fileName: string;
    fileSize: string;
    uploadedAt: string;
    uploadedBy: string;
  }>;
}

interface WhatsAppContentSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onContentSelect: (processedMessage: string) => void;
  currentLead: Lead;
  addFiles?: (files: File[]) => void;
}

const WhatsAppContentSelector: React.FC<WhatsAppContentSelectorProps> = ({
  isOpen,
  onClose,
  onContentSelect,
  currentLead,
  addFiles,
}) => {
  // 🔥 DIRECT API STATE - No Redux!
  const [allContent, setAllContent] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Original state for UI
  const [selectedContent, setSelectedContent] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'file' | 'page'>('file');  // 🔥 REMOVED: message, whatsapp_template
  const [searchInput, setSearchInput] = useState<string>('');

  // 🔥 Enhanced state for file operations
  const [sendMode, setSendMode] = useState<'link' | 'upload'>('link');
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [downloadProgress, setDownloadProgress] = useState<{[key: string]: number}>({});

  // 🔥 DIRECT API FILTERING - No Redux selector
  const filteredContent = useMemo(() => {
    let filtered = allContent.filter(item => item.type === activeTab);
    if (searchInput.trim()) {
      const searchTerm = searchInput.toLowerCase();
      filtered = filtered.filter(item => {
        const name = item.name || item.details?.title || '';
        const description = item.details?.description || '';
        return name.toLowerCase().includes(searchTerm) || description.toLowerCase().includes(searchTerm);
      });
    }
    return filtered;
  }, [allContent, activeTab, searchInput]);

  // 🔥 DIRECT API CALL - No Redux dispatch
  useEffect(() => {
    if (isOpen) {
      const fetchContent = async () => {
        setLoading(true);
        setError(null);
        try {
          logger.log('📥 Content Selector opened, calling API directly...');
          const response = await getAllContent({ page: 1, perPage: 500 });
          
          // 🔥 FILTER: Only file and page content, no message/template
          const fileAndPageContent = (response.data.data || []).filter((item: any) => 
            item.type === 'file' || item.type === 'page'
          );
          
          setAllContent(fileAndPageContent);
          logger.log('✅ Direct API: Successfully fetched', fileAndPageContent.length, 'file/page items');
          logger.log('🔍 Direct API: Sample item structure:', fileAndPageContent[0]);
        } catch (err: any) {
          setError(err.message || 'Failed to fetch content');
          logger.log('❌ Direct API: Error fetching content:', err);
        } finally {
          setLoading(false);
        }
      };
      fetchContent();
    }
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedContent(null);
      setSelectedFiles(new Set());
      setDownloadingFiles(new Set());
      setDownloadProgress({});
      setSendMode('link');
    }
  }, [isOpen]);

  const handleTabChange = (newTab: typeof activeTab) => {
    logger.log('🔄 Tab changed to:', newTab);
    setActiveTab(newTab);
    setSelectedContent(null);
    setSelectedFiles(new Set());
  };

  // 🔥 File selection handlers
  const handleFileToggle = useCallback((fileKey: string) => {
    setSelectedFiles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(fileKey)) {
        newSet.delete(fileKey);
      } else {
        newSet.add(fileKey);
      }
      logger.log('📋 File selection updated:', newSet.size, 'files selected');
      return newSet;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allFileKeys = new Set<string>();
    filteredContent.forEach(content => {
      if (content.files) {
        content.files.forEach(file => {
          allFileKeys.add(`${content._id}:${file.fileName}`);
        });
      }
    });
    setSelectedFiles(allFileKeys);
    logger.log('📋 Selected all files:', allFileKeys.size);
  }, [filteredContent]);

  const handleClearSelection = useCallback(() => {
    setSelectedFiles(new Set());
    logger.log('📋 Cleared file selection');
  }, []);

  // 🔥 CRITICAL FIX: Background download and upload with proper filePath
  const handleBulkFileUpload = useCallback(async () => {
    if (!addFiles || selectedFiles.size === 0) {
      logger.log('⚠️ Cannot upload: no addFiles function or no files selected');
      return;
    }

    const selectedFileArray = Array.from(selectedFiles).map(fileKey => {
      const [contentId, fileName] = fileKey.split(':');
      
      // 🔥 CRITICAL: Find file info from DIRECT API data
      const contentItem = filteredContent.find(c => c._id === contentId);
      const fileInfo = contentItem?.files?.find(f => f.fileName === fileName);
      
      logger.log('🔍 DIRECT API DEBUG: File mapping:', {
        contentId,
        fileName,
        contentItem: contentItem ? {
          _id: contentItem._id,
          type: contentItem.type,
          filesCount: contentItem.files?.length || 0
        } : 'CONTENT_NOT_FOUND',
        fileInfo: fileInfo ? {
          filePath: fileInfo.filePath,
          fileName: fileInfo.fileName,
          fileSize: fileInfo.fileSize
        } : 'FILE_INFO_NOT_FOUND'
      });
      
      // 🔥 CRITICAL: Use filePath from direct API
      const actualFilePath = fileInfo?.filePath;
      
      if (!actualFilePath) {
        logger.log(`❌ CRITICAL: No filePath found for ${fileName} in content ${contentId}`);
      }
      
      return { 
        contentId, 
        fileName, 
        fileKey,
        filePath: actualFilePath,
        fileInfo
      };
    });

    const downloadedFiles: File[] = [];
    let successCount = 0;

    logger.log(`🚀 Starting bulk download: ${selectedFileArray.length} files`);
    toast.loading(`Downloading ${selectedFileArray.length} files...`, { id: 'bulk-download' });

    for (const { contentId, fileName, fileKey, filePath } of selectedFileArray) {
      try {
        setDownloadingFiles(prev => new Set([...prev, fileKey]));
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 10 }));

        // 🔥 CRITICAL VALIDATION: Ensure filePath exists and is valid
        if (!filePath) {
          throw new Error(`No file path found for ${fileName}. Check API response structure.`);
        }
        
        if (!filePath.startsWith('content/')) {
          throw new Error(`Invalid file path format: ${filePath}. Expected format: content/contentId/filename.ext`);
        }

        logger.log(`📥 Downloading: ${fileName}`);
        logger.log(`🛤️ Using file path: ${filePath}`);

        // 🔥 PROPER CALL: Use filePath (full S3 path) and fileName (display name)
        const blob = await downloadContentFile(filePath, fileName);
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 70 }));

        // Validate the downloaded blob
        if (!blob || blob.size === 0) {
          throw new Error(`Downloaded file is empty: ${fileName}`);
        }

        // Convert to File object with proper MIME type
        const file = contentBlobToFile(blob, fileName);
        downloadedFiles.push(file);
        
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 100 }));
        successCount++;

        logger.log(`✅ Downloaded: ${fileName} (${file.size} bytes, ${file.type})`);

      } catch (error: any) {
        logger.log(`❌ Failed to download: ${fileName}`, {
          error: error.message,
          filePath,
          contentId,
          stack: error.stack
        });
        
        // More specific error messages for user
        if (error.message.includes('404')) {
          toast.error(`File not found: ${fileName}`);
        } else if (error.message.includes('Network Error')) {
          toast.error(`Network error downloading: ${fileName}`);
        } else {
          toast.error(`Failed to download: ${fileName}`);
        }
        
        setDownloadProgress(prev => ({ ...prev, [fileKey]: 0 }));
      } finally {
        setDownloadingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(fileKey);
          return newSet;
        });
      }
    }

    toast.dismiss('bulk-download');

    // 🔥 RESULT HANDLING: Add successfully downloaded files to upload queue
    if (downloadedFiles.length > 0) {
      logger.log(`📤 Adding ${downloadedFiles.length} files to upload queue`);
      addFiles(downloadedFiles);
      
      const successMessage = successCount === selectedFiles.size 
        ? `✅ All ${successCount} files added to upload queue`
        : `⚠️ ${successCount} of ${selectedFiles.size} files added to upload queue`;
      
      toast.success(successMessage);
      
      // Clear selection and close modal
      setSelectedFiles(new Set());
      setDownloadProgress({});
      onClose();
    } else {
      toast.error('❌ No files were downloaded successfully. Check console for details.');
    }
  }, [selectedFiles, addFiles, onClose, filteredContent]);

  // 🔥 SIMPLIFIED: Only file and page content processing
  const processSelectedContent = async (content: ContentItem): Promise<string> => {
    logger.log('⚙️ Processing content:', content);
    
    try {
      const leadName = currentLead.name || 'Customer';
      
      logger.log('🔗 Generating unique link for:', content.type);
      
      const body = {
        contentId: content._id,
        leadIds: [currentLead._id],
        performedAt: new Date(),
        contentType: content.type,
      };
      
      const response = await shareContentLead(body);
      
      if (!response?.data?.data?.uniqueLink) {
        throw new Error('No unique link generated');
      }
      
      const processedMessage = `Hi ${leadName},

Here's the link to view:
${response.data.data.uniqueLink}`;
      
      logger.log('✅ Generated link message:', processedMessage);
      return processedMessage;
      
    } catch (error) {
      logger.log('❌ Error processing content:', error);
      throw error;
    }
  };

  const handleContentSelect = async () => {
    if (!selectedContent) {
      logger.log('⚠️ No content selected');
      return;
    }
    
    try {
      logger.log('🎯 Handling content selection:', selectedContent);
      
      const content = filteredContent.find(item => item._id === selectedContent);
      if (!content) {
        logger.log('❌ Selected content not found:', selectedContent);
        return;
      }
      
      const processedMessage = await processSelectedContent(content);
      logger.log('📤 Sending processed message to parent:', processedMessage);
      
      onContentSelect(processedMessage);
      onClose();
      
    } catch (error) {
      logger.log('❌ Error selecting content:', error);
      toast.error('Failed to process selected content');
    }
  };

  const handleContentClick = (contentId: string) => {
    if (sendMode === 'link') {
      logger.log('👆 Content clicked for link mode:', contentId);
      setSelectedContent(contentId);
    }
  };

  const renderContentPreview = (content: ContentItem) => {
    if (content.type === 'file' && content.files && content.files.length > 0) {
      return `File: ${content.files[0].fileName}`;
    }
    
    return content.details?.description || 'No preview available';
  };

  // 🔥 Enhanced content card rendering
  const renderEnhancedContentCard = useCallback((content: ContentItem) => {
    const isSelected = content._id === selectedContent;
    const title = content.name || content.details?.title || 'Untitled';
    
    // Handle file content differently in upload mode
    if (sendMode === 'upload' && content.files) {
      return (
        <div key={content._id} style={{
          padding: '16px',
          border: '2px solid #E5E7EB',
          borderRadius: '8px',
          backgroundColor: 'white',
          marginBottom: '12px'
        }}>
          <div style={{ 
            fontSize: '14px', 
            fontWeight: 600, 
            marginBottom: '12px',
            color: '#1F2937'
          }}>
            📁 {title}
          </div>
          
          {/* File list with checkboxes */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {content.files?.map((fileInfo, index) => {
              const fileKey = `${content._id}:${fileInfo.fileName}`;
              const isDownloading = downloadingFiles.has(fileKey);
              const progress = downloadProgress[fileKey] || 0;
              const isFileSelected = selectedFiles.has(fileKey);
              
              return (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px',
                  backgroundColor: isFileSelected ? '#F0F9FF' : '#F9FAFB',
                  borderRadius: '6px',
                  border: isFileSelected ? '1px solid #3FAEFD' : '1px solid #E5E7EB',
                  transition: 'all 0.2s ease'
                }}>
                  <input
                    type="checkbox"
                    checked={isFileSelected}
                    onChange={() => handleFileToggle(fileKey)}
                    disabled={isDownloading}
                    style={{ 
                      cursor: isDownloading ? 'not-allowed' : 'pointer',
                      transform: 'scale(1.1)'
                    }}
                  />
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>
                      📄 {fileInfo.fileName}
                    </div>
                    
                    {isDownloading && (
                      <div style={{ marginTop: '4px' }}>
                        <div style={{
                          width: '100%',
                          height: '4px',
                          backgroundColor: '#E5E7EB',
                          borderRadius: '2px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${progress}%`,
                            height: '100%',
                            backgroundColor: '#3FAEFD',
                            transition: 'width 0.3s ease'
                          }} />
                        </div>
                        <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>
                          Downloading... {progress}%
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    
    // Original card rendering for link mode and non-file content
    return (
      <div
        key={content._id}
        onClick={() => handleContentClick(content._id)}
        style={{
          padding: '16px',
          border: `2px solid ${isSelected ? '#3FAEFD' : '#E5E7EB'}`,
          borderRadius: '8px',
          backgroundColor: isSelected ? '#F0F9FF' : 'white',
          cursor: sendMode === 'link' ? 'pointer' : 'default',
          height: '200px',
          display: 'flex',
          flexDirection: 'column',
          transition: 'all 0.2s ease',
          boxShadow: isSelected ? '0 4px 8px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.05)',
        }}
      >
        <div style={{
          fontSize: '14px',
          fontWeight: 600,
          color: '#1F2937',
          marginBottom: '8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}>
          {title}
        </div>
        
        <div style={{
          fontSize: '12px',
          color: '#059669',
          marginBottom: '8px',
          textTransform: 'uppercase',
          fontWeight: 500
        }}>
          {content.type}
        </div>
        
        <div style={{
          fontSize: '13px',
          color: '#6B7280',
          flex: 1,
          overflow: 'hidden',
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical'
        }}>
          {renderContentPreview(content)}
        </div>
      </div>
    );
  }, [sendMode, selectedFiles, downloadingFiles, downloadProgress, selectedContent, handleFileToggle, handleContentClick]);

  // 🔥 Mode selector component
  const renderModeSelector = () => (
    <div style={{
      display: 'flex',
      gap: '8px',
      marginBottom: '16px',
      padding: '4px',
      backgroundColor: '#F3F4F6',
      borderRadius: '8px'
    }}>
      <button
        onClick={() => setSendMode('link')}
        style={{
          flex: 1,
          padding: '8px 16px',
          backgroundColor: sendMode === 'link' ? '#3FAEFD' : 'transparent',
          color: sendMode === 'link' ? 'white' : '#6B7280',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          transition: 'all 0.2s ease'
        }}
      >
        📎 Send Links
      </button>
      
      <button
        onClick={() => setSendMode('upload')}
        disabled={!addFiles}
        style={{
          flex: 1,
          padding: '8px 16px',
          backgroundColor: sendMode === 'upload' ? '#3FAEFD' : 'transparent',
          color: sendMode === 'upload' ? 'white' : addFiles ? '#6B7280' : '#D1D5DB',
          border: 'none',
          borderRadius: '6px',
          cursor: addFiles ? 'pointer' : 'not-allowed',
          fontSize: '14px',
          fontWeight: 500,
          transition: 'all 0.2s ease'
        }}
      >
        📤 Upload Files
      </button>
    </div>
  );

  // 🔥 Bulk actions component
  const renderBulkActions = () => {
    if (sendMode !== 'upload') return null;
    
    const totalFiles = filteredContent.reduce((count, content) => 
      count + (content.files?.length || 0), 0
    );
    
    const isDownloading = downloadingFiles.size > 0;
    
    return (
      <div style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: '#F8FAFC',
        borderRadius: '8px',
        border: '1px solid #E2E8F0'
      }}>
        <button
          onClick={handleSelectAll}
          disabled={isDownloading || totalFiles === 0}
          style={{
            padding: '6px 12px',
            backgroundColor: 'white',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: (isDownloading || totalFiles === 0) ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            color: '#374151'
          }}
        >
          Select All ({totalFiles})
        </button>
        
        <button
          onClick={handleClearSelection}
          disabled={selectedFiles.size === 0 || isDownloading}
          style={{
            padding: '6px 12px',
            backgroundColor: 'white',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            cursor: (selectedFiles.size === 0 || isDownloading) ? 'not-allowed' : 'pointer',
            fontSize: '12px',
            color: '#374151'
          }}
        >
          Clear Selection
        </button>
        
        <div style={{ flex: 1 }} />
        
        <div style={{ fontSize: '12px', color: '#6B7280' }}>
          {selectedFiles.size} of {totalFiles} files selected
        </div>
        
        <button
          onClick={handleBulkFileUpload}
          disabled={selectedFiles.size === 0 || isDownloading}
          style={{
            padding: '8px 16px',
            backgroundColor: selectedFiles.size === 0 || isDownloading ? '#D1D5DB' : '#10B981',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (selectedFiles.size === 0 || isDownloading) ? 'not-allowed' : 'pointer',
            fontSize: '13px',
            fontWeight: 500
          }}
        >
          {isDownloading 
            ? `Downloading... (${downloadingFiles.size})` 
            : `📤 Upload Selected (${selectedFiles.size})`
          }
        </button>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div style={{ 
      textAlign: 'center', 
      padding: '40px', 
      color: '#6B7280',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      flex: 1
    }}>
      <div style={{ fontSize: '48px', marginBottom: '16px' }}>📂</div>
      <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No content found</h3>
      <p style={{ margin: 0 }}>Try adjusting your search or switching tabs</p>
    </div>
  );

  // 🔥 Calculate counts from direct API data
  const counts = useMemo(() => ({
    file: allContent.filter(item => item.type === 'file').length,
    page: allContent.filter(item => item.type === 'page').length,
  }), [allContent]);

  logger.log('🎯 WhatsAppContentSelector: Direct API state loaded', {
    allContentCount: allContent.length,
    loading,
    counts,
    currentLead,
    sendMode,
    selectedFilesCount: selectedFiles.size
  });

  return (
    <PopupComponent
      title="Select Content"
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '80vh' }}>
        
        {/* Mode Selector */}
        {renderModeSelector()}
        
        {/* Bulk Actions for Upload Mode */}
        {renderBulkActions()}
        
        {/* Tab Navigation - Only file and page */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #E5E7EB',
          marginBottom: '16px'
        }}>
          {(['file', 'page'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                padding: '12px 16px',
                backgroundColor: activeTab === tab ? '#3FAEFD' : 'transparent',
                color: activeTab === tab ? 'white' : '#6B7280',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px 6px 0 0',
                marginRight: '4px',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.8 }}>
                ({counts[tab]})
              </span>
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text"
            placeholder={`Search ${activeTab}s...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '14px',
              outline: 'none'
            }}
          />
        </div>

        {/* Error Display */}
        {error && (
          <div style={{
            color: '#EF4444',
            textAlign: 'center',
            margin: '16px 0',
            padding: '12px',
            backgroundColor: '#FEF2F2',
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            {error}
            <button
              onClick={() => {
                setError(null);
                // Trigger refetch
                const fetchContent = async () => {
                  setLoading(true);
                  try {
                    const response = await getAllContent({ page: 1, perPage: 500 });
                    const fileAndPageContent = (response.data.data || []).filter((item: any) => 
                      item.type === 'file' || item.type === 'page'
                    );
                    setAllContent(fileAndPageContent);
                  } catch (err: any) {
                    setError(err.message || 'Failed to fetch content');
                  } finally {
                    setLoading(false);
                  }
                };
                fetchContent();
              }}
              style={{
                display: 'block',
                margin: '8px auto 0',
                background: 'none',
                border: 'none',
                color: '#2563EB',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div style={{ 
            padding: '40px 0',
            textAlign: 'center',
            color: '#6B7280',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1
          }}>
            <div style={{ marginBottom: '16px' }}>
              <i className="ri-loader-4-line" style={{ 
                fontSize: '32px', 
                animation: 'spin 1s linear infinite'
              }}></i>
            </div>
            <div>Loading...</div>
          </div>
        ) : (
          /* Content Grid */
          <div style={{ 
            flex: 1,
            overflowY: 'auto',
            padding: '4px'
          }}>
            {filteredContent.length > 0 ? (
              <div style={{ 
                display: sendMode === 'upload' ? 'block' : 'grid',
                gridTemplateColumns: sendMode === 'link' ? 'repeat(auto-fill, minmax(300px, 1fr))' : 'none',
                gap: '16px',
                padding: '8px',
              }}>
                {filteredContent.map(renderEnhancedContentCard)}
              </div>
            ) : (
              renderEmptyState()
            )}
          </div>
        )}

        {/* Action Buttons for Link Mode */}
        {sendMode === 'link' && (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px', 
            padding: '16px 0', 
            borderTop: '1px solid #E5E7EB',
            marginTop: '16px'
          }}>
            <button
              onClick={onClose}
              style={{
                padding: '10px 20px',
                backgroundColor: '#F3F4F6',
                color: '#374151',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleContentSelect}
              disabled={!selectedContent}
              style={{
                padding: '10px 20px',
                backgroundColor: selectedContent ? '#3FAEFD' : '#D1D5DB',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: selectedContent ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              Send Link
            </button>
          </div>
        )}
      </div>
    </PopupComponent>
  );
};

export default WhatsAppContentSelector;